import { useRef, useEffect } from 'react';
import { useGetState } from 'ahooks';
import { fetchVideos } from '../services';
import { transformVideos, isVideoAndCoverProcessing } from '../utils';
import { UploadStatus } from '../enums';

import type { IFilter, IVideo, IVideosInfos } from '../types';

type ITimer = ReturnType<typeof setInterval | typeof setTimeout>;

const INTERVAL = 10 * 1000;

const getVideosInfosByVideos = (videos: IVideo[]): IVideosInfos => {
  if (!Array.isArray(videos)) return {};
  const map: IVideosInfos = {};
  for (const video of videos) {
    const uuid = video?.uuid;
    if (!uuid) continue;
    map[uuid] = {
      status: video?.status || UploadStatus.Loading,
      result_video: video?.result_video || '',
      result_video_url: video?.result_video_url || '',
      concat_video: video?.concat_video || '',
      concat_video_url: video?.concat_video_url || '',
      concat_video_cover: video?.concat_video_cover || '',
      result_msg: video?.result_msg || '',
      create_time: video?.create_time || '',
    };
  }
  return map;
};

export default function useVideosPollingEffect(props: { filter: IFilter; videos: IVideo[]; }) {
  const { filter, videos } = props;

  const timer = useRef<ITimer | null>(null);

  const [videosInfos, setVideosInfos, getVideosInfos] = useGetState<IVideosInfos>({});

  const refresh = async () => {
    try {
      const uuidsOfProcessingVideos = videos
        ?.filter((video) => isVideoAndCoverProcessing(video?.status))
        ?.map((video) => video?.uuid);
      const { motion_video_info: motionVideoInfo } = await fetchVideos({
        uuid: uuidsOfProcessingVideos || [],
        role_model_type: filter?.role_model_type,
      });
      if (Array.isArray(motionVideoInfo)) {
        const motionVideoInfoTmp = transformVideos(motionVideoInfo);
        const videosInfosTmp = getVideosInfosByVideos(motionVideoInfoTmp);
        setVideosInfos({
          ...videosInfos,
          ...videosInfosTmp,
        });

        const hasProcessingVideos = motionVideoInfoTmp?.some((item) => isVideoAndCoverProcessing(item?.status));
        if (!hasProcessingVideos) {
          clearTimer();
        }
      }
    } catch (exception) { }
  };

  const startTimer = () => {
    timer.current = setInterval(() => {
      refresh();
    }, INTERVAL);
  };

  const clearTimer = () => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    setVideosInfos(getVideosInfosByVideos(videos));
    const hasProcessingVideos = videos?.some((video) => isVideoAndCoverProcessing(video?.status));
    if (hasProcessingVideos) {
      startTimer();
    }

    return () => {
      clearTimer();
    };
  }, [filter, videos]);

  return [videosInfos, getVideosInfos] as [IVideosInfos, () => IVideosInfos];
}
